<template>
  <v-form ref="form" v-model="formValid">
    <v-radio-group v-model="localValue.regionOption" inline hide-details class="mb-5">
      <v-radio :value="RegionOption.MOVE_TO_ANOTHER" class="mr-6">
        <template #label>
          <span class="body2">{{ $t("network.regionUpdate.moveToAnotherRegion") }}</span>
        </template>
      </v-radio>
      <v-radio :value="RegionOption.KEEP_CURRENT">
        <template #label>
          <span class="body2">{{ $t("network.regionUpdate.keepCurrentRegion") }}</span>
        </template>
      </v-radio>
    </v-radio-group>

    <template v-if="localValue.regionOption === RegionOption.KEEP_CURRENT">
      <div class="info-block d-flex align-center mb-4 py-1 px-4">
        <span class="body2">
          {{ $t("network.regionUpdate.currentLocation", { location: config.item.region }) }}
        </span>
      </div>
      <v-checkbox v-model="localValue.item.preserveIp" hide-details class="mt-0 mb-2">
        <template #label>
          <span class="body2 ml-2">{{ $t("network.regionUpdate.preserveIpLabel") }}</span>
        </template>
      </v-checkbox>
      <div class="body2 mb-7 preserve-hint">
        {{ $t("network.regionUpdate.preserveIpHint") }}
      </div>
    </template>

    <template v-else>
      <div class="warning-block d-flex align-start mt-2 mb-5 py-1 px-4">
        <v-icon class="mr-3" icon="$warning" />
        <span class="body2">
          {{ $t("network.regionUpdate.selectRegionDisclaimer") }}
        </span>
      </div>
      <div class="subtitle1 mb-4">{{ $t("network.regionUpdate.selectRegionTitle") }}</div>
      <v-autocomplete
        v-model="localValue.item.region"
        :items="sortedRegions"
        menu-icon="icon-arrow-down-xs"
        item-title="displayValue"
        item-value="value"
        :rules="[requiredRule, sameValueRule]"
        :label="$t('network.regionUpdate.selectRegionPlaceholder')"
        :placeholder="$t('network.initialSetup.selectRegionPlaceholder')"
        variant="outlined"
        class="mb-10"
        @update:model-value="shouldValidateSameValue = true"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <span class="ml-1">{{ item.raw.displayValue }}</span>
          </v-list-item>
        </template>
      </v-autocomplete>
    </template>

    <v-select
      v-model="localValue.item.subnet"
      :items="subnetOptions"
      menu-icon="icon-arrow-down-xs"
      :rules="[required()]"
      :label="$t('network.initialSetup.selectSubnetLabel')"
      :placeholder="$t('network.initialSetup.selectSubnetPlaceholder')"
      :persistent-placeholder="false"
      :hint="$t('network.initialSetup.defaultSubnetHint')"
      persistent-hint
      variant="outlined"
      class="mb-5"
    >
      <template #message="{ message }">
        <div class="d-flex align-center">
          <v-icon size="x-small" icon="$infoXs" class="mr-1" />
          <span>{{ message }}</span>
        </div>
      </template>
    </v-select>
    <div class="subtitle1 mb-4">
      {{ $t("modals.setupVirtualOffice.confirmInputLabel") }}
    </div>
    <v-text-field
      v-model="localValue.approveText"
      class="mt-6"
      variant="outlined"
      :rules="matchApproveRules"
      :aria-label="$t('modals.setupVirtualOffice.confirmInputLabel')"
    />
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { VuetifyFormRef } from "@/types";
import {
  DEFAULT_VIRTUAL_OFFICE_SUBNET,
  RegionOption,
  VIRTUAL_OFFICE_SUBNET_OPTIONS,
} from "@/constants/network.ts";
import { required } from "@/_helpers/validators.ts";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module.ts";

type Region = { value: string; displayValue: string };

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<{ region: string; subnet?: string }>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const form = ref<VuetifyFormRef>();
    const i18n = useI18n();
    const formValid = ref(true);
    const regions = ref<Record<string, string>>({});
    const shouldValidateSameValue = ref(false);
    const virtualOfficeStore = useVirtualOfficeStore();

    const subnetOptions = VIRTUAL_OFFICE_SUBNET_OPTIONS;

    const localValue = ref<{
      regionOption: RegionOption;
      approveText: string;
      item: { region?: string; preserveIp: boolean; subnet: string };
    }>({
      regionOption: RegionOption.MOVE_TO_ANOTHER,
      approveText: "",
      item: {
        region: undefined,
        preserveIp: false,
        subnet: props.config.item.subnet ?? DEFAULT_VIRTUAL_OFFICE_SUBNET,
      },
    });

    const sortedRegions = computed(() =>
      Object.entries(regions.value)
        .map(([value, displayValue]): Region => ({ value, displayValue }))
        .sort((a, b) => a.displayValue.localeCompare(b.displayValue))
    );

    const currentRegionValue = computed(
      () =>
        sortedRegions.value.find(({ displayValue }) => displayValue === props.config.item.region)
          ?.value ?? ""
    );

    const requiredRule = (v: string | undefined) => (!v ? i18n.t("validations.required") : true);

    const sameValueRule = (v: string | undefined) => {
      if (!shouldValidateSameValue.value) return true;
      const displayValue = sortedRegions.value.find((r) => r.value === v)?.displayValue;
      return displayValue === props.config.item.region ? i18n.t("validations.sameValue") : true;
    };

    const matchApproveRules = computed(() => [
      (v?: string) => {
        if (!v || !v.length) return i18n.t("validations.typeApprove.required");
        if (v !== i18n.t("validations.typeApprove.approve").toUpperCase()) {
          return i18n.t("validations.typeApprove.exactMatch");
        }
        return true;
      },
    ]);

    watch(localValue, (newVal) => emit("update:localValue", newVal), { deep: true });

    watch(formValid, (value) => {
      if (value === null) return;
      emit("update:valid", value);
    });

    watch(
      () => localValue.value.regionOption,
      async (option) => {
        localValue.value.approveText = "";
        if (option === RegionOption.KEEP_CURRENT) {
          shouldValidateSameValue.value = false;
          localValue.value.item.region = currentRegionValue.value;
        } else {
          localValue.value.item.preserveIp = false;
          localValue.value.item.region = undefined;
        }
        await nextTick();
        form.value?.resetValidation();
      }
    );

    const validate = async () => {
      shouldValidateSameValue.value = true;
      const validationResult = await form.value?.validate();
      return validationResult?.valid;
    };

    onMounted(async () => {
      regions.value = await virtualOfficeStore.getVpnRegions();
      await nextTick();
      form.value?.resetValidation();
    });

    return {
      RegionOption,
      required,
      form,
      formValid,
      localValue,
      sortedRegions,
      subnetOptions,
      shouldValidateSameValue,
      requiredRule,
      sameValueRule,
      matchApproveRules,
      validate,
    };
  },
});
</script>

<style lang="scss" scoped>
.info-block {
  background-color: rgb(var(--v-theme-indigo-pale));
  border-radius: 4px;
}

.preserve-hint {
  margin-left: 38px;
  color: rgb(var(--v-theme-indigo-medium));
}
</style>
